import { Component, Inject, Injector, Input, OnChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlContainer, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, NgModel } from '@angular/forms';
import { AppTextService } from '@fi-shared/app-text';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

@Component({
    selector: 'fi-checklist-edit-checkpoint-name',
    templateUrl: './checkpoint-name.component.html',
    styleUrls: ['./checkpoint-name.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ChecklistEditCheckpointNameComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: ChecklistEditCheckpointNameComponent,
        },
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: NgForm,
        },
    ],
    host: { class: 'fi-checklist-edit-checkpoint-name' },
    standalone: false,
})
export class ChecklistEditCheckpointNameComponent
    extends ControlValueAccessorValidatorBase<string>
    implements OnChanges
{
    @Input() placeholder: string;
    @Input() required: boolean;
    @Input() disabled: boolean;
    @Input() touched: boolean = false;
    @Input() name: string;
    @Input() contentLanguageId: number;
    @Input() checklist: ChecklistModel;
    @Input() autofocus: boolean = false;

    @ViewChild('nameModel', { static: true }) nameModel: NgModel;

    static next: number = 0;
    protected n = 0;

    constructor(
        private injector: Injector,
        @Inject('defaultLanguageId') private defaultLanguageId,
        private appTextService: AppTextService,
        private contentLanguages: ContentLanguagesService,
        private session: SessionService,
        private form: NgForm,
        private userDataService: FiUserDataService,
    ) {
        super(injector);

        this.n = ++ChecklistEditCheckpointNameComponent.next;
    }

    onNgModelChange($event) {
        this.doPropagateChanges();
    }

    ngOnChanges(changes) {
        if (changes.touched && this.touched) {
            this.nameModel.control.markAsTouched();
        }
    }

    setTouched() {
        this.onTouched();
        this.nameModel.control.markAsTouched();
    }

    isShowNameError() {
        if (this.contentLanguageId === this.defaultLanguageId && !this.form.submitted) {
            return false;
        }

        return this.nameModel.invalid;
    }

    getNameErrorText() {
        if (this.ngModel) return '';

        // checkpoint name is required in default language
        if (this.contentLanguageId === this.defaultLanguageId) {
            return this.appTextService.get('common_error.mandatory');
        } else {
            const defaultLang = this.contentLanguages.GetDefaultLanguage(),
                chosenLang = this.contentLanguages.GetContentLanguageById(this.contentLanguageId),
                nameWithLang = this.checklist.Names.find((name) => name.ContentLanguageId == this.contentLanguageId),
                descriptionWithLang = this.checklist.Descriptions.find(
                    (description) => description.ContentLanguageId == this.contentLanguageId,
                );

            // checkpoint title is required if checklist name or description filled in this language
            if ((nameWithLang && nameWithLang.Value) || (descriptionWithLang && descriptionWithLang.Value)) {
                return this.appTextService.get('checklist.content_name_required_by_name', {
                    '{chosen_language}': chosenLang.Name.toLowerCase(),
                });
            }

            // it is required to fill out all checkpoints names in language if at least one of them is filled out
            // not obvious check here because this logic is in "required" validator and this message comes from the logic in validator
            const lowerCaseLanguages = this.userDataService.applicationLanguage == FiApplicationLanguageType.Swedish;
            return this.appTextService.get('checklist.content_name_either_required', {
                '{chosen_language}': lowerCaseLanguages ? chosenLang.Name.toLowerCase() : chosenLang.Name,
                '{default_language}': lowerCaseLanguages ? defaultLang.Name.toLowerCase() : defaultLang.Name,
            });
        }
    }

    getCheckpointModelInputName() {
        return `CheckpointName_input+${this.n}`;
    }
}
